import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useSession } from '../auth/session.tsx';
import { fitFor } from './map-fit.ts';
import { DEFAULT_CENTRE, DEFAULT_ZOOM, SINGLE_ZOOM, MIN_ZOOM, MAX_ZOOM, WHEEL_ZOOM, authenticatedTiles, escapeHtml } from './CaseloadMap.tsx';

/**
 * Pins on our own tiles, for a list that has places (the Real Estate map,
 * 14 October). The visit map's tile layer (tiles from our API, never a third
 * party), its fit rule and its two default views, shared rather than copied.
 *
 * FILTER FIRST, THEN FIT: `pins` is already the filtered list; the view fits
 * when `fitKey` changes and at no other time, so a reload keeps where the
 * reader has panned.
 *
 * PIN STYLE IS A LOOKUP, so a colour key can be added without reworking the
 * map: each pin names a `kind`, and `styles` maps a kind to its look and its
 * label. A key, when one is wanted, is drawn from the same table. Today the
 * Real Estate map has one kind, a blue dot, and draws no key.
 */
export interface Pin {
  id: string;
  latitude: number | string | null;
  longitude: number | string | null;
  /** Bold first line of the tooltip. */
  title: string;
  /** Second line. */
  detail?: string;
  kind: string;
  /** A few characters drawn on the pin itself, as the itinerary's stop numbers are. */
  badge?: string;
}

export interface PinStyle {
  label: string;
  fill: string;
  edge: string;
  radius: number;
}

export function PinMap({
  pins, styles, fitKey, onOpen, label, showKey = false, fill = false,
}: {
  pins: Pin[];
  styles: Record<string, PinStyle>;
  fitKey: string;
  onOpen: (pin: Pin) => void;
  label: string;
  showKey?: boolean;
  /** Take the height of the box it is placed in, rather than its own fixed height (the itinerary's map column). */
  fill?: boolean;
}) {
  const { getToken } = useSession();
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const markers = useRef<L.LayerGroup | null>(null);
  const fitted = useRef<string | null>(null);
  const [outside, setOutside] = useState(0);
  const token = useRef(getToken);
  token.current = getToken;
  const open = useRef(onOpen);
  open.current = onOpen;

  useEffect(() => {
    if (!container.current || map.current) return;
    const instance = L.map(container.current, {
      center: DEFAULT_CENTRE, zoom: DEFAULT_ZOOM, minZoom: MIN_ZOOM, maxZoom: MAX_ZOOM, scrollWheelZoom: WHEEL_ZOOM,
    });
    instance.addLayer(authenticatedTiles(() => token.current()));
    markers.current = L.layerGroup().addTo(instance);
    map.current = instance;
    // Leaflet measures its box once; a box that changes size (a column sized to the window) is measured again.
    const resized = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => instance.invalidateSize({ animate: false }));
    resized?.observe(container.current);
    return () => {
      resized?.disconnect();
      instance.remove();
      map.current = null;
      markers.current = null;
    };
  }, []);

  useEffect(() => {
    const layer = markers.current;
    const instance = map.current;
    if (!layer || !instance) return;
    layer.clearLayers();
    const points: [number, number][] = [];
    for (const pin of pins) {
      const lat = Number(pin.latitude);
      const lon = Number(pin.longitude);
      if (pin.latitude === null || pin.longitude === null || !Number.isFinite(lat) || !Number.isFinite(lon)) continue;
      points.push([lat, lon]);
      const style = styles[pin.kind] ?? Object.values(styles)[0]!;
      const marker = L.circleMarker([lat, lon], {
        radius: style.radius, color: style.edge, weight: 1.5, fillColor: style.fill, fillOpacity: 0.9,
      });
      marker.bindTooltip(`<strong>${escapeHtml(pin.title)}</strong>${pin.detail ? `<br>${escapeHtml(pin.detail)}` : ''}`, { direction: 'top' });
      marker.on('click', () => open.current(pin));
      marker.on('keypress', (event) => {
        const original = (event as unknown as { originalEvent?: KeyboardEvent }).originalEvent;
        if (original?.key === 'Enter' || original?.key === ' ') open.current(pin);
      });
      layer.addLayer(marker);
      // Drawn over the pin and not interactive, so the pin's own tooltip and click still answer.
      if (pin.badge) {
        layer.addLayer(L.marker([lat, lon], {
          icon: L.divIcon({ className: 'pin-badge', html: escapeHtml(pin.badge), iconSize: [24, 24] }),
          interactive: false, keyboard: false,
        }));
      }
    }
    if (fitKey === fitted.current) return;
    fitted.current = fitKey;
    const fit = fitFor(points);
    if (!fit) instance.setView(DEFAULT_CENTRE, DEFAULT_ZOOM, { animate: false });
    else if ('centre' in fit) instance.setView(fit.centre, SINGLE_ZOOM, { animate: false });
    else instance.fitBounds(L.latLngBounds(fit.box), { padding: [40, 40], maxZoom: 14, animate: false });
    const view = instance.getBounds();
    setOutside(points.filter((p) => !view.contains(p)).length);
  }, [pins, styles, fitKey]);

  return (
    <div className={fill ? 'relative flex h-full flex-col' : 'relative'}>
      <div ref={container} role="region" aria-label={label}
           className={fill ? 'min-h-0 w-full flex-1 rounded-lg bg-neutral-200' : 'h-[32rem] w-full rounded-lg bg-neutral-200 lg:h-[40rem]'} />
      {showKey && (
        <ul className="mt-2 flex flex-wrap gap-4 text-sm text-neutral-700" aria-label="Key">
          {Object.entries(styles).map(([k, s]) => (
            <li key={k} className="flex items-center gap-2">
              <span className="inline-block size-3 rounded-full" style={{ background: s.fill, border: `1.5px solid ${s.edge}` }} />
              {s.label}
            </li>
          ))}
        </ul>
      )}
      {outside > 0 && (
        <p role="status" className="mt-2 text-sm text-neutral-700">
          {outside} {outside === 1 ? 'pin was' : 'pins were'} left outside the map as it opened, far from the rest. Zoom out to see {outside === 1 ? 'it' : 'them'}.
        </p>
      )}
    </div>
  );
}
